"use client";

import { useEffect, useState, Suspense, memo } from "react";
import dynamic from 'next/dynamic';
import useTranslations from "../../hooks/useTranslation";
import useIsMobile from "../../hooks/useIsMobile";
import { useNavbar } from "../../contexts/NavbarContext";

const Navbar = dynamic(() => import('./navbar'), { 
    suspense: true,
    ssr: false 
});

function NavbarClient() {
    const {
        loading,
        translations,
        supportedLanguages,
        language,
        selectedLanguage,
        handleLanguageChange,
    } = useTranslations();

    const [mounted, setMounted] = useState(false);
    const isMobile = useIsMobile();
    
    // Prevent hydration mismatch by waiting for client-side mount
    useEffect(() => {
        setMounted(true);
    }, []);
    
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { companyRef, productsRef, blogRef, contactusRef, scrollToSection } = useNavbar();
    
    // Always render navbar once mounted - use cached translations from localStorage
    // The useTranslations hook loads from cache first, so translations should be available immediately
    const isReady = mounted;

    useEffect(() => {
        if (sidebarOpen) {
            document.body.classList.add('no-scroll');
        } else {
            document.body.classList.remove('no-scroll');
        }

        return () => {
            document.body.classList.remove('no-scroll');
        };
    }, [sidebarOpen]);

    // Render consistent placeholder on both server and client until ready
    if (!isReady) {
        return (
            <div 
                style={{ 
                    height: '100px',
                    width: '100%',
                    backgroundColor: 'var(--bg-primary)',
                    position: 'fixed',
                    top: 0,
                    zIndex: 1000
                }} 
                suppressHydrationWarning
            />
        );
    }
    
    return (
        <div suppressHydrationWarning>
            <Suspense fallback={
                <div 
                    style={{ 
                        height: '100px',
                        width: '100%',
                        backgroundColor: 'var(--bg-primary)',
                        position: 'fixed',
                        top: 0,
                        zIndex: 1000
                    }} 
                />
            }>
                <Navbar
                    isMobile={isMobile}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                    scrollToSection={scrollToSection}
                    companyRef={companyRef}
                    productsRef={productsRef}
                    contactusRef={contactusRef}
                    blogRef={blogRef}
                    language={language}
                    translations={translations}
                    supportedLanguages={supportedLanguages}
                    selectedLanguage={selectedLanguage}
                    handleLanguageChange={handleLanguageChange}
                />
            </Suspense>
        </div>
    );
}

// Export without memo to allow React to handle re-renders naturally
// The component will only re-render when its dependencies (from hooks) change
export default NavbarClient;

